import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { PUBLISHED_TXT, TOAST_CLASSES } from "../utils/constants";
import {
  fetchAllAgents,
  fetchAgentsDetails,
  registerAgent as registerAgentAPI,
  updateExistingAgent,
  deleteAgent as deleteAgentAPI,
  publishAgent as publishAgentAPI,
  getAgentCategoriesAPI,
  getAgentAllVersionsAPI,
} from "../apis/agent.details.api";
import { AuthContext } from "../AuthContext";
import { Backdrop, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "./DashboardContext";
import { getLookupValuesAPI } from "../apis/common.api";

const AgentContext = createContext();

const getInitialAgentData = () => ({
  agent_name: "",
  agent_description: "",
  sample_prompt: "",
  point_of_contact: "",
  LLM_model: "",
  url: "",
  active: true,
  agent_metadata: {},
  approval_status: "",
  rejection_reason: "",
  delete_agent: 0,
  type: "",
  classifications: [
    {
      industry_info: [
        {
          industry: "",
          sector: [],
        },
      ],
      business_function: [],
      skills: [],
    },
  ],
  metadata: [
    {
      version_number: "",
      documentation_url: "",
      provider: "",
    },
    {
      streaming: true,
      push_notifications: true,
      state_transition_history: true,
    },
  ],
});

export const AgentsProvider = ({ children }) => {
  const { showToastMessage } = useDashboard();
  const [isFetchingAgents, setIsFetchingAgents] = useState(false);
  const [isRegisteringOrEditingAgent, setIsRegisteringOrEditingAgent] =
    useState(false);
  const [isSilentLoading, setIsSilentLoading] = useState(false);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isApprovingRejecting, setIsApprovingRejecting] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isFetchingSectorsSilently, setIsFetchingSectorsSilently] =
    useState(false);
  const [isInitializingEdit, setIsInitializingEdit] = useState(false);

  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const initialAgentData = getInitialAgentData();
  const [ongoingAgentData, setOngoingAgentData] = useState(initialAgentData);

  const [agentCardOpen, setAgentCardOpen] = useState(false);

  const [showLoader, setShowLoader] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isLoadingDropdownFields, setIsLoadingDropdownFields] = useState({
    providers: false,
    industries: false,
    sectors: false,
    llms: false,
    bFs: false,
    status: false,
  });

  const [lookUpValues, setLookUpValues] = useState({
    providers: [],
    industries: [],
    sectors: [],
    llms: [],
    bFs: [],
    status: [],
  });

  const [agentVersions, setAgentVersions] = useState([]);
  const [currentAgentVersion, setCurrentAgentVersion] = useState(null);

  const [versionsFetchError, setVersionsFetchError] = useState(false);

  const [agentDetailsDrawerOpen, setAgentDetailsDrawerOpen] = useState(false);

  const [isLatestVersion, setIsLatestVersion] = useState(false);

  // Required fields validation state
  const [requiredFields, setRequiredFields] = useState({
    agent_name: false,
    agent_description: false,
    point_of_contact: false,
    sample_prompt: false,
    documentation_url: false,
    skill: false,
  });

  const { organisationId, projectId, authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;

  const navigate = useNavigate();
  // Show backdrop spinner
  const showSpinner = useCallback((isOpen) => {
    setShowLoader(isOpen);
  }, []);

  const changeAgentDrawerState = useCallback((bool) => {
    setAgentDetailsDrawerOpen(bool);
  }, []);

  const validateIndustrySector = useCallback((industryInfo) => {
    const updatedIndustryInfo = [];

    for (const item of industryInfo) {
      let updatedIndustry = item?.industry;
      let updatedSector = item?.sector;

      if (updatedSector && typeof updatedIndustry !== "string") {
        updatedIndustry = "";
      } else if (typeof updatedIndustry === "string" && !updatedSector) {
        updatedSector = [];
      }

      updatedIndustryInfo.push({
        industry: updatedIndustry,
        sector: updatedSector,
      });
    }

    return updatedIndustryInfo;
  }, []);

  // ------------------------------ API Functions START ------------------------------

  const fetchAgents = useCallback(async () => {
    setIsFetchingAgents(true);

    try {
      const response = await fetchAllAgents({
        queryKey: [
          "agents",
          { organisationId, projectId, access_token, token_type },
        ],
      });

      if (!response) {
        setAgents([]);
        showToastMessage("No agents found", TOAST_CLASSES?.ERROR);
      } else {
        setAgents(response?.data || []);

        return response;
      }
    } catch (err) {
      showToastMessage("Failed to fetch agents", TOAST_CLASSES?.ERROR);
      setAgents([]);
      console.error("Error fetching agents:", err);
    } finally {
      setIsFetchingAgents(false);
    }
  }, [organisationId, projectId, access_token, token_type, showToastMessage]);

  const fetchAgentsSilently = useCallback(async () => {
    setIsSilentLoading(true);

    try {
      const response = await fetchAllAgents({
        queryKey: [
          "agents",
          { organisationId, projectId, access_token, token_type },
        ],
      });

      if (response) {
        setAgents(response?.data || agents);
      } else {
        showToastMessage("Failed to fetch latest data.", TOAST_CLASSES?.ERROR);
      }
    } catch (err) {
      showToastMessage("Failed to fetch latest data.", TOAST_CLASSES?.ERROR);
      console.error("Error fetching agents in background:", err);
    } finally {
      setIsSilentLoading(false);
    }
  }, [
    organisationId,
    projectId,
    access_token,
    token_type,
    agents,
    showToastMessage,
  ]);

  const deleteAgent = useCallback(
    async (agentId) => {
      try {
        setIsDeleting(true);

        await deleteAgentAPI({
          id: agentId,
          organisationId,
          projectId,
          access_token,
          token_type,
        });

        showToastMessage(
          "Agent has been deleted successfully.",
          TOAST_CLASSES?.SUCCESS
        );
        await fetchAgentsSilently();
      } catch (err) {
        showToastMessage("Failed to delete agent", TOAST_CLASSES?.ERROR);
        console.error("Error deleting agent:", err);
      } finally {
        setIsDeleting(false);
      }
    },
    [
      organisationId,
      projectId,
      access_token,
      token_type,
      showToastMessage,
      fetchAgentsSilently,
    ]
  );

  const fetchAgentBFs = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({ ...prev, bFs: true }));
    try {
      const response = await getAgentCategoriesAPI({
        queryKey: [
          "agent-categories",
          { organisationId, projectId, access_token, token_type },
        ],
      });

      if (response) {
        const categories = [
          ...response?.map((category) => {
            if (typeof category === "string") {
              return category;
            }
            return category.lookup_value || String(category);
          }),
        ];

        setLookUpValues((prev) => ({ ...prev, bFs: categories }));
        return categories;
      } else {
        setLookUpValues((prev) => ({ ...prev, bFs: [] }));
        return [];
      }
    } catch (err) {
      console.error("Error fetching agent categories:", err);
      return [];
    } finally {
      setIsLoadingDropdownFields((prev) => ({ ...prev, bFs: false }));
    }
  }, [access_token, organisationId, projectId, token_type]);

  const fetchAgentProviders = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      providers: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "agent_provider");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch providers", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((provider) => provider?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        providers: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching solution providers");
      showToastMessage(
        "Failed to fetch solution providers",
        TOAST_CLASSES?.ERROR
      );
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        providers: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchAgentLLMs = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      llms: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "agent_llm");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch LLMs", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((llm) => llm?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        llms: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching agent LLMs");
      showToastMessage("Failed to fetch agent LLMs", TOAST_CLASSES?.ERROR);
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        llms: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchAgentIndustries = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      industries: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "industry");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch industries", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((industry) => industry?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        industries: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching agent industries");
      showToastMessage(
        "Failed to fetch agent industries",
        TOAST_CLASSES?.ERROR
      );
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        industries: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchAgentSectors = useCallback(
    async (industry) => {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        sectors: true,
      }));
      try {
        const headData = {
          organisationId,
          projectId,
          access_token,
          token_type,
        };

        const response = await getLookupValuesAPI(
          headData,
          `industry:${industry}`
        );

        if (!response || !response?.data) {
          showToastMessage("Failed to fetch sectors", TOAST_CLASSES?.ERROR);
          return;
        }

        const data = response?.data?.map((sector) => sector?.lookup_value);

        setLookUpValues((prev) => ({
          ...prev,
          sectors: data || [],
        }));

        return data || [];
      } catch {
        console.error("Error fetching agent sectors");
        showToastMessage("Failed to fetch agent sectors", TOAST_CLASSES?.ERROR);
      } finally {
        setIsLoadingDropdownFields((prev) => ({
          ...prev,
          sectors: false,
        }));
      }
    },
    [access_token, organisationId, projectId, token_type, showToastMessage]
  );

  const fetchAgentSectorsSilently = useCallback(
    async (industry) => {
      setIsFetchingSectorsSilently(true);
      try {
        const headData = {
          organisationId,
          projectId,
          access_token,
          token_type,
        };

        const response = await getLookupValuesAPI(
          headData,
          `industry:${industry}`
        );

        if (!response || !response?.data) {
          showToastMessage("Failed to fetch sectors", TOAST_CLASSES?.ERROR);
          return;
        }

        const data = response?.data?.map((sector) => sector?.lookup_value);

        return data || [];
      } catch {
        console.error("Error fetching agent sectors");
        showToastMessage("Failed to fetch agent sectors", TOAST_CLASSES?.ERROR);
      } finally {
        setIsFetchingSectorsSilently(false);
      }
    },
    [access_token, organisationId, projectId, token_type, showToastMessage]
  );

  const fetchAgentStatus = useCallback(async () => {
    setIsLoadingDropdownFields((prev) => ({
      ...prev,
      status: true,
    }));
    try {
      const headData = { organisationId, projectId, access_token, token_type };

      const response = await getLookupValuesAPI(headData, "agent_status");

      if (!response || !response?.data) {
        showToastMessage("Failed to fetch status", TOAST_CLASSES?.ERROR);
        return;
      }

      const data = response?.data?.map((status) => status?.lookup_value);

      setLookUpValues((prev) => ({
        ...prev,
        status: data || [],
      }));

      return data || [];
    } catch {
      console.error("Error fetching agent status");
      showToastMessage("Failed to fetch agent status", TOAST_CLASSES?.ERROR);
    } finally {
      setIsLoadingDropdownFields((prev) => ({
        ...prev,
        status: false,
      }));
    }
  }, [access_token, organisationId, projectId, token_type, showToastMessage]);

  const fetchAgentVersions = useCallback(
    async (agentId) => {
      try {
        setIsLoadingVersions(true);
        setVersionsFetchError(false);

        const response = await getAgentAllVersionsAPI(
          {
            id: agentId,
            organisationId,
            projectId,
            access_token,
            token_type,
          },
          agentId
        );
        if (response) {
          setAgentVersions(response?.data || []);
          setVersionsFetchError(false);
          return response?.data;
        } else {
          setVersionsFetchError(true);

          showToastMessage(
            "Failed to fetch versions, showing current version.",
            TOAST_CLASSES?.ERROR
          );
        }
      } catch (err) {
        console.error("failed to fetch version: ", err);
        setVersionsFetchError(true);
        showToastMessage(
          "Failed to fetch versions, showing current version.",
          TOAST_CLASSES?.ERROR
        );
      } finally {
        setIsLoadingVersions(false);
      }
    },
    [organisationId, projectId, access_token, token_type, showToastMessage]
  );

  // Check if all required fields are valid
  const areAllRequiredFieldsValid = useCallback(() => {
    const isValid = Object.values(requiredFields).every((isValid) => isValid);
    return isValid;
  }, [requiredFields]);

  const registerAgent = useCallback(async () => {
    if (areAllRequiredFieldsValid()) {
      const payload = ongoingAgentData;

      const industryInfo = payload?.classifications?.[0]?.industry_info || [];

      const updatedIndustryInfo = validateIndustrySector(industryInfo);

      if (payload?.classifications?.[0]) {
        payload.classifications[0].industry_info = updatedIndustryInfo;
      }

      try {
        setIsRegisteringOrEditingAgent(true);

        const response = await registerAgentAPI({
          organisationId,
          projectId,
          access_token,
          token_type,
          payload: payload,
        });

        if (response) {
          showToastMessage(
            "Agent has been registered successfully.",
            TOAST_CLASSES?.SUCCESS
          );
        } else {
          showToastMessage("Failed to register agent", TOAST_CLASSES?.ERROR);
        }
      } catch (err) {
        showToastMessage("Failed to register agent.", TOAST_CLASSES?.ERROR);
      } finally {
        setIsRegisteringOrEditingAgent(false);
        navigate("/agents", { replace: true });
      }
    } else {
      showToastMessage("Please fill all required fields", TOAST_CLASSES?.ERROR);
    }
  }, [
    areAllRequiredFieldsValid,
    organisationId,
    projectId,
    access_token,
    token_type,
    ongoingAgentData,
    showToastMessage,
    navigate,
    validateIndustrySector,
  ]);

  const editAgent = useCallback(
    async (agentId) => {
      if (areAllRequiredFieldsValid()) {
        const payload = ongoingAgentData;

        const industryInfo = payload?.classifications?.[0]?.industry_info || [];

        const updatedIndustryInfo = validateIndustrySector(industryInfo);

        if (payload?.classifications?.[0]) {
          payload.classifications[0].industry_info = updatedIndustryInfo;
        }

        try {
          setIsRegisteringOrEditingAgent(true);

          const response = await updateExistingAgent({
            organisationId,
            projectId,
            access_token,
            token_type,
            agent_id: agentId,
            payload: payload,
          });

          if (response) {
            showToastMessage(
              "Agent Edits has been saved successfully.",
              TOAST_CLASSES?.SUCCESS
            );
          } else {
            showToastMessage("Failed to edit agent.", TOAST_CLASSES?.ERROR);
          }
        } catch (err) {
          showToastMessage("Failed to edit agent.", TOAST_CLASSES?.ERROR);
        } finally {
          setIsRegisteringOrEditingAgent(false);
          navigate("/agents", { replace: true });
        }
      } else {
        showToastMessage(
          "Please fill all required fields",
          TOAST_CLASSES?.ERROR
        );
      }
    },
    [
      areAllRequiredFieldsValid,
      organisationId,
      projectId,
      access_token,
      token_type,
      ongoingAgentData,
      showToastMessage,
      navigate,
      validateIndustrySector,
    ]
  );

  const getAgent = useCallback(
    async (agentId) => {
      try {
        setIsFetchingAgents(true); // Show backdrop spinner

        const response = await fetchAgentsDetails({
          queryKey: [
            "agent-details",
            {
              agent_id: agentId,
              organisationId,
              projectId,
              access_token,
              token_type,
            },
          ],
        });

        return response;
      } catch (err) {
        console.error("Error fetching agent details:", err);
        showToastMessage("Failed to fetch agent details", TOAST_CLASSES?.ERROR);
        return null;
      } finally {
        setIsFetchingAgents(false); // Hide backdrop spinner
      }
    },
    [organisationId, projectId, access_token, token_type, showToastMessage]
  );

  const publishAgent = useCallback(
    async (agentId, approvalStatus, rejectionReason) => {
      try {
        setIsApprovingRejecting(true);

        const payloadData = {
          approval_status: approvalStatus,
          rejection_reason: rejectionReason || "",
        };
        const response = await publishAgentAPI({
          organisationId,
          projectId,
          access_token,
          token_type,
          agent_id: agentId,
          payload: payloadData,
        });

        if (response) {
          showToastMessage(
            `Agent has been ${approvalStatus?.toLowerCase() === PUBLISHED_TXT?.toLowerCase()
              ? "published"
              : "unpublished"
            } successfully.`,
            TOAST_CLASSES?.SUCCESS
          );
        } else {
          showToastMessage(
            `Failed to  ${approvalStatus?.toLowerCase() === PUBLISHED_TXT?.toLowerCase()
              ? "publish"
              : "unpublish"
            } the agent.`,
            TOAST_CLASSES?.ERROR
          );
        }
      } catch (err) {
        showToastMessage(
          `Failed to  ${approvalStatus?.toLowerCase() === PUBLISHED_TXT?.toLowerCase()
            ? "publish"
            : "unpublish"
          } the agent.`,
          TOAST_CLASSES?.ERROR
        );
      } finally {
        setIsApprovingRejecting(false);
        changeAgentDrawerState(false);
      }
    },
    [
      organisationId,
      projectId,
      access_token,
      token_type,
      showToastMessage,
      changeAgentDrawerState,
    ]
  );

  // ------------------------------- API Functions END ------------------------------

  // Validation function for required fields
  const validateRequiredField = useCallback((field, value) => {
    let isValid = false;

    switch (field) {
      case "agent_name":
      case "point_of_contact":
      case "sample_prompt":
        isValid = !!value && value.trim() !== "";
        break;
      case "agent_description":
        // Check for empty rich text content
        isValid =
          !!value &&
          value.trim() !== "" &&
          value !== "<p></p>" &&
          value !== "<p><br></p>";
        break;
      case "documentation_url":
        isValid = !!value && value.trim() !== "";
        break;
      case "skill":
        isValid = Array.isArray(value) && value.length > 0;
        break;
      default:
        isValid = !!value;
    }

    setRequiredFields((prev) => ({
      ...prev,
      [field]: isValid,
    }));
  }, []);

  // Reset validation state
  const resetValidation = useCallback(() => {
    setRequiredFields({
      agent_name: false,
      agent_description: false,
      point_of_contact: false,
      sample_prompt: false,
      documentation_url: false,
      skill: false,
    });
  }, []);

  // Function to set agents (for loading from API)
  const setAgentsData = useCallback((agentsData) => { }, []);

  const selectAgent = useCallback((agent) => {
    setSelectedAgent(agent);
  }, []);

  const selectAgentVersion = useCallback((version) => {
    setCurrentAgentVersion(version);
  }, []);

  const clearSelectedAgent = useCallback(() => {
    setSelectedAgent(null);
  }, []);

  const clearAgentVersions = useCallback(() => {
    setAgentVersions(null);
  }, []);

  const clearCurrentAgentVersion = useCallback(() => {
    setCurrentAgentVersion(null);
  }, []);

  // Function to update ongoing agent data
  const updateOngoingAgentDataL1 = useCallback((field, data) => {
    setOngoingAgentData((prevData) => ({
      ...prevData,
      [field]: data,
    }));
  }, []);

  const updateOngoingAgentDataL2 = useCallback((field, data) => {
    setOngoingAgentData((prevData) => ({
      ...prevData,
      metadata: [
        { ...prevData?.metadata?.[0], [field]: data },
        prevData?.metadata?.[1],
      ],
    }));
  }, []);

  const updateOngoingAgentDataL3 = useCallback(
    (field, data) => {
      if (field === "business_function") {
        setOngoingAgentData((prevData) => ({
          ...prevData,
          classifications: [
            {
              ...prevData?.classifications?.[0],
              business_function: data,
            },
          ],
        }));
      } else if (field === "industry_info") {
        setOngoingAgentData((prevData) => ({
          ...prevData,
          classifications: [
            {
              ...prevData?.classifications?.[0],
              industry_info: data,
            },
          ],
        }));
      } else if (field === "skills") {
        setOngoingAgentData((prevData) => ({
          ...prevData,
          classifications: [
            {
              ...prevData?.classifications?.[0],
              skills: data,
            },
          ],
        }));

        // Validate the skill field immediately after state update
        setTimeout(() => {
          validateRequiredField("skill", data);
        }, 0);
      }
    },
    [validateRequiredField]
  );

  // Function to clear ongoing agent data
  const clearOngoingAgentData = useCallback(() => {
    setOngoingAgentData(getInitialAgentData());
    resetValidation();
  }, [resetValidation]);

  // Helper function to directly set immediate data from agent for form initialization
  const fillImmediateAgentData = useCallback(
    (agentData) => {
      if (!agentData) {
        clearOngoingAgentData();
        return;
      }

      const docUrl = agentData?.agent_metadata?.a2a?.documentationUrl || "";

      // temporary fix for skills
      const skillsArr =
        agentData?.agent_metadata?.a2a?.skills?.map((skill) => skill?.name) ||
        [];

      const versionNo = agentData?.agent_metadata?.a2a?.version || "";

      updateOngoingAgentDataL1("agent_name", agentData?.agent_name || "");
      updateOngoingAgentDataL1(
        "agent_description",
        agentData?.agent_description || ""
      );
      updateOngoingAgentDataL1("sample_prompt", agentData?.sample_prompt || "");
      updateOngoingAgentDataL1(
        "point_of_contact",
        agentData?.point_of_contact || ""
      );
      updateOngoingAgentDataL1("LLM_model", ""); // will be set after api call
      updateOngoingAgentDataL1("url", agentData?.url || "");
      updateOngoingAgentDataL1("active", agentData?.active || true);
      updateOngoingAgentDataL1(
        "approval_status",
        agentData?.approval_status || ""
      );
      updateOngoingAgentDataL1(
        "rejection_reason",
        agentData?.rejection_reason || ""
      );
      updateOngoingAgentDataL1("delete_agent", agentData?.delete_agent || 0);
      updateOngoingAgentDataL1("type", agentData?.type || "");

      updateOngoingAgentDataL2("documentation_url", docUrl);
      updateOngoingAgentDataL2("provider", ""); // will be set after api call
      updateOngoingAgentDataL2("version_number", versionNo);

      updateOngoingAgentDataL3("industry_info", []); // will be set after api call
      updateOngoingAgentDataL3("skills", skillsArr);

      // Validate immediate fields after setting
      setTimeout(() => {
        validateRequiredField("agent_name", agentData?.agent_name || "");
        validateRequiredField(
          "point_of_contact",
          agentData?.point_of_contact || ""
        );
        validateRequiredField(
          "agent_description",
          agentData?.agent_description || ""
        );
        validateRequiredField("sample_prompt", agentData?.sample_prompt || "");
        validateRequiredField("documentation_url", docUrl);
        validateRequiredField("skill", skillsArr);
      }, 100);
    },
    [
      updateOngoingAgentDataL1,
      updateOngoingAgentDataL2,
      updateOngoingAgentDataL3,
      validateRequiredField,
      clearOngoingAgentData,
    ]
  );

  const fetchSetFilteredLLMs = useCallback(
    async (agentData) => {
      const llm = agentData?.LLM_model;

      const response = await fetchAgentLLMs();
      const llmsFromApi = response || [];

      const validLLM = llmsFromApi?.find((l) => l === llm);

      updateOngoingAgentDataL1("LLM_model", validLLM || "");
    },
    [updateOngoingAgentDataL1, fetchAgentLLMs]
  );

  const fetchSetFilteredBFs = useCallback(
    async (agentData) => {
      const businessFunctionArr =
        agentData?.agent_metadata?.a2a?.business_function;

      const response = await fetchAgentBFs();
      const bFsFromApi = response || [];

      const filteredBFs = businessFunctionArr?.filter((bF) =>
        bFsFromApi?.includes(bF)
      );

      updateOngoingAgentDataL3("business_function", filteredBFs);
    },
    [updateOngoingAgentDataL3, fetchAgentBFs]
  );

  const fetchFilteredIndustryInfo = useCallback(
    async (agentData) => {
      const industryInfoArr =
        agentData?.agent_metadata?.a2a?.industry_info || [];

      const industriesFromApi = (await fetchAgentIndustries()) || [];

      const filteredISs =
        industryInfoArr?.filter((iS) =>
          industriesFromApi?.includes(iS?.industry)
        ) || [];

      let finalISs = [];
      if (filteredISs?.length > 0) {
        try {
          const updatedIndustryInfo = await Promise.allSettled(
            filteredISs?.map(async (item, index) => {
              const sectorValuesFromAPI = await fetchAgentSectorsSilently(
                item?.industry
              );

              return { ...item, sectorValues: sectorValuesFromAPI || [] };
            })
          );

          finalISs = updatedIndustryInfo?.map((item) => item?.value);
        } catch { }
      }

      const blankObj = { industry: "", sector: [], sectorValues: [] };

      if (finalISs?.length < 3) {
        while (finalISs.length < 3) {
          finalISs.push(blankObj);
        }
      }

      updateOngoingAgentDataL3("industry_info", finalISs);

      return finalISs;
    },
    [fetchAgentIndustries, updateOngoingAgentDataL3, fetchAgentSectorsSilently]
  );

  const fetchFilteredProviders = useCallback(
    async (agentData) => {
      const provider = agentData?.agent_metadata?.a2a?.provider;

      const response = await fetchAgentProviders();
      const providersFromApi = response || [];

      const filteredProvider =
        providersFromApi?.find((p) => p === provider) || "";

      updateOngoingAgentDataL2("provider", filteredProvider);
    },
    [fetchAgentProviders, updateOngoingAgentDataL2]
  );

  const fetchFilteredStatus = useCallback(
    async (agentData) => {
      const status = agentData?.approval_status;

      const response = await fetchAgentStatus();
      const providersFromApi = response || [];

      const filteredStatus = providersFromApi?.find((p) => p === status) || "";

      updateOngoingAgentDataL1("status", filteredStatus);
    },
    [fetchAgentStatus, updateOngoingAgentDataL1]
  );

  // Function to initialize edit mode with selected agent data
  const initializeEdit = useCallback(
    async (agentData) => {
      if (!agentData) {
        console.warn("No agent data provided for edit initialization");
        return;
      }
      try {
        setIsInitializingEdit(true);

        fillImmediateAgentData(agentData);

        const loadAsyncData = async () => {
          await Promise.all([
            fetchFilteredIndustryInfo(agentData),
            fetchFilteredProviders(agentData),
            fetchSetFilteredBFs(agentData),
            fetchSetFilteredLLMs(agentData),
            fetchFilteredStatus(agentData),
          ]);
        };

        await loadAsyncData();
      } catch (error) {
        console.error("Error initializing edit mode:", error);
      } finally {
        setIsInitializingEdit(false);
      }
    },
    [
      fillImmediateAgentData,
      fetchSetFilteredBFs,
      fetchFilteredIndustryInfo,
      fetchFilteredProviders,
      fetchSetFilteredLLMs,
      fetchFilteredStatus,
    ]
  );

  useEffect(() => {
    // console.log("ongoing agent data: ", ongoingAgentData);
  }, [ongoingAgentData]);

  const value = {
    agents,
    selectedAgent,
    ongoingAgentData,
    isSilentLoading,
    requiredFields,
    agentCardOpen,
    agentVersions,
    currentAgentVersion,
    isLoadingVersions,
    isApprovingRejecting,
    versionsFetchError,
    isUnpublishing,
    agentDetailsDrawerOpen,
    isLatestVersion,
    isLoadingDropdownFields,
    lookUpValues,
    isInitializingEdit,
    isFetchingSectorsSilently,
    fetchAgentSectorsSilently,
    fetchAgentStatus,
    fetchAgentLLMs,
    fetchAgentIndustries,
    fetchAgentSectors,
    fetchAgentProviders,
    setIsLatestVersion,
    changeAgentDrawerState,
    setIsUnpublishing,
    publishAgent,
    fetchAgentVersions,
    clearCurrentAgentVersion,
    selectAgentVersion,
    clearAgentVersions,
    setAgentCardOpen,
    fetchAgentBFs,
    fetchAgents,
    fetchAgentsSilently,
    editAgent,
    getAgent,
    deleteAgent,
    setAgentsData,
    selectAgent,
    clearSelectedAgent,
    registerAgent,
    clearOngoingAgentData,
    initializeEdit,
    updateOngoingAgentDataL1,
    updateOngoingAgentDataL2,
    updateOngoingAgentDataL3,
    showSpinner,
    validateRequiredField,
    areAllRequiredFieldsValid,
    resetValidation,
  };

  return (
    <AgentContext.Provider value={value}>
      {children}

      <Backdrop
        sx={{
          color: "var(--db-primary-white)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: "134vh !important",
        }}
        open={
          showLoader ||
          isFetchingAgents ||
          isDeleting ||
          isRegisteringOrEditingAgent ||
          isApprovingRejecting
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </AgentContext.Provider>
  );
};

// Custom hook for using the context
export const useAgent = () => {
  const context = useContext(AgentContext);

  if (!context) {
    throw new Error("useAgent must be used within an AgentsProvider");
  }

  return context;
};

export default AgentContext;
